import React, { useEffect, useState } from 'react';
import { ConnectionsApi } from '../../apis';
import { getEntityId, getLinkToTab } from '../../utils';
import { defaultFont, themedText } from '../../styles/SharedStyles';
import { useThemeContext } from '../../context';
import * as Constants from '../../utils/constants';
import { parseEntityId } from '../../utils/parsers';
import { PwWebConection } from '../../types';
import { LoadingPage } from '../Shared';
import { pages } from "@microsoft/teams-js";

export function TabRemoval(): JSX.Element {
  const [tabName, setTabName] = useState<string>();
  const [connectionsWithDefaultTab, setConnectionsWithDefaultTab] =
    useState<PwWebConection[]>();

  const theme = useThemeContext();

  async function getConnectionsWithDefaultTab(): Promise<PwWebConection[]> {
    const entityId = await getEntityId();
    const { connectionId, serviceId, projectId } = parseEntityId(entityId);

    if (
      !connectionId &&
      serviceId !== Constants.ServiceRegistry.projectWise365Id
    ) {
      return [];
    }

    const linkToTab = await getLinkToTab(entityId);
    try {
      if (serviceId !== Constants.ServiceRegistry.projectWise365Id) {
        const connection = await ConnectionsApi.fetchConnectionDetails(
          projectId,
          connectionId!
        );
        return connection?.defaultTab === linkToTab ? [connection] : [];
      } else {
        const connections = await ConnectionsApi.fetchConnections(projectId);
        return connections.filter(
          connection => connection.defaultTab === linkToTab
        );
      }
    } catch {
      return [];
    }
  }

  async function deleteDefaultTabForConnections(
    connections: PwWebConection[]
  ): Promise<boolean | undefined> {
    const entityId = await getEntityId();
    const { projectId } = parseEntityId(entityId);

    return ConnectionsApi.deleteDefaultTabForConnections(
      projectId,
      connections.map(connection => connection.id)
    );
  }

  async function onRemove(
    removeEvent: microsoftTeams.settings.RemoveEvent
  ): Promise<void> {
    try {
      if (connectionsWithDefaultTab && connectionsWithDefaultTab.length > 0) {
        await deleteDefaultTabForConnections(connectionsWithDefaultTab);
      }
    } catch (e) {
      removeEvent.notifyFailure(e);
      console.error(e);
    }

    removeEvent.notifySuccess();
  }

  useEffect(() => {
    async function initializePage() {
      try {
        microsoftTeams.settings.registerOnRemoveHandler(onRemove);
        const settings = await pages.getConfig();
        if ((settings as any).suggestedTabName) {
          setTabName((settings as any).suggestedTabName);
        }
        setTabName(settings.suggestedDisplayName);

        const connections = await getConnectionsWithDefaultTab();
        setConnectionsWithDefaultTab(connections);

        microsoftTeams.settings.setValidityState(true);
      } catch (e) {
        console.error('Error while mounting component: ', e);
      }
    }

    initializePage().catch(error => console.error(error));
  }, []);

  if (!connectionsWithDefaultTab) {
    return <LoadingPage subtext='Loading tab configuration...' />;
  }

  return (
    <div style={{ ...defaultFont, ...themedText(theme) }}>
      <p>
        {tabName
          ? `The "${tabName}" tab will be permanently removed`
          : 'The tab will be permanently removed.'}
      </p>
      {connectionsWithDefaultTab.length > 0 && (
        <p>
          This is the default tab for related ProjectWise Web connection(s). Any
          existing links to this tab will no longer work.
        </p>
      )}
    </div>
  );
}
